import { navigate } from 'gatsby';
import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Modal from '~/components/base/Modal';
import Button from '~/components/base/Button';

import Spinner from '~/components/Spinner';

import OutlineButton from '~/components/buttons/OutlineButton';
import { AlertIcon, FormError } from '~/components/form/layoutHelpers';

import subscriptions from '~/account/api/subscriptions';
import { getErrorMessage } from '~/account/utilities/helpers';

import { h2 } from '~/styles/typography';

const ModalTitle = styled.h2`
    ${h2};
    color: ${({ theme }) => theme.colors.primary.red};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-bottom: 45px;
    }
`;

const ButtonsWrapper = styled.div`
    display: flex;
    gap: 25px;
    margin-top: 25px;
`;

const goBack = () => {
    navigate('/account/garages');
};

const confirmUpdateReducer = (state, action) => {
    switch (action.type) {
        case 'SUCCESS':
            return { ...state, didUpdate: true, error: null, loading: false };
        case 'ERROR':
            return {
                ...state,
                loading: false,
                error: getErrorMessage(action.error),
                didUpdate: false
            };
        case 'LOADING':
            return { ...state, loading: true, didUpdate: false, error: null };
        default:
            return state;
    }
};

const ConfirmUpdateSubscriptionModal = ({
    open,
    setOpen,
    vehicleId,
    garageId,
    priceId,
    subscriptionName
}) => {
    const [state, dispatch] = useReducer(confirmUpdateReducer, {
        didUpdate: false,
        error: null,
        loading: false
    });

    const updateSubscription = async (vehicleId, priceId) => {
        dispatch({ type: 'LOADING' });
        const response = await subscriptions.updateSubscription(
            vehicleId,
            priceId,
            garageId
        );
        if (response.success) {
            dispatch({ type: 'SUCCESS' });
        } else {
            dispatch({ type: 'ERROR', error: response.errorCode });
        }
    };

    return (
        <Modal {...{ open, setOpen }} onClose={state.didUpdate ? goBack : null}>
            {state.didUpdate ? (
                <>
                    <ModalTitle>Your subscription has been updated.</ModalTitle>
                    <Button onClick={goBack}>Back</Button>
                </>
            ) : (
                <>
                    <ModalTitle>
                        Are you sure you want to change your plan to{' '}
                        {subscriptionName}?
                    </ModalTitle>
                    {state.error && (
                        <FormError>
                            <AlertIcon aria-hidden="true" />
                            <span aria-live="assertive">{state.error}</span>
                        </FormError>
                    )}
                    {state.loading ? (
                        <Spinner />
                    ) : (
                        <ButtonsWrapper>
                            <Button
                                onClick={() =>
                                    updateSubscription(vehicleId, priceId)
                                }
                            >
                                Yes
                            </Button>
                            <OutlineButton onClick={() => setOpen(false)}>
                                No
                            </OutlineButton>
                        </ButtonsWrapper>
                    )}
                </>
            )}
        </Modal>
    );
};

ConfirmUpdateSubscriptionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    vehicleId: PropTypes.number.isRequired,
    garageId: PropTypes.string.isRequired,
    priceId: PropTypes.number.isRequired,
    subscriptionName: PropTypes.string.isRequired
};

export default ConfirmUpdateSubscriptionModal;
